import {
  Reservation,
  ReservationCreate,
  ReservationCalendar,
  ReservationConflictCheck,
} from '../types/reservation';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:8006';

export const reservationApi = {
  // 예약 생성
  async createReservation(
    reservationData: ReservationCreate,
    userId: string
  ): Promise<Reservation> {
    const response = await fetch(`${API_BASE_URL}/reservations/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...reservationData,
        user_id: userId,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '예약 생성에 실패했습니다');
    }

    return response.json();
  },

  // 사용자 예약 목록 조회
  async getUserReservations(
    userId: string,
    page: number = 1,
    size: number = 10,
    status?: string
  ): Promise<{
    reservations: Reservation[];
    total: number;
    page: number;
    size: number;
    total_pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (status) {
      params.append('status', status);
    }

    const response = await fetch(
      `${API_BASE_URL}/reservations/user/${userId}?${params}`
    );

    if (!response.ok) {
      throw new Error('예약 목록 조회에 실패했습니다');
    }

    return response.json();
  },

  // 예약 달력 조회
  async getReservationCalendar(date: string): Promise<ReservationCalendar> {
    const response = await fetch(
      `${API_BASE_URL}/reservations/calendar/${date}`
    );

    if (!response.ok) {
      throw new Error('예약 달력 조회에 실패했습니다');
    }

    return response.json();
  },

  // 예약 충돌 검사
  async checkReservationConflict(
    seatId: string,
    startTime: string,
    endTime: string,
    excludeReservationId?: string
  ): Promise<ReservationConflictCheck> {
    const params = new URLSearchParams({
      seat_id: seatId,
      start_time: startTime,
      end_time: endTime,
    });

    if (excludeReservationId) {
      params.append('exclude_reservation_id', excludeReservationId);
    }

    const response = await fetch(
      `${API_BASE_URL}/reservations/conflict-check?${params}`
    );

    if (!response.ok) {
      throw new Error('예약 충돌 검사에 실패했습니다');
    }

    return response.json();
  },

  // 예약 수정
  async updateReservation(
    reservationId: string,
    updateData: Partial<
      ReservationCreate & { status?: string; cancellation_reason?: string }
    >
  ): Promise<Reservation> {
    const response = await fetch(
      `${API_BASE_URL}/reservations/${reservationId}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '예약 수정에 실패했습니다');
    }

    return response.json();
  },

  // 예약 취소
  async cancelReservation(
    reservationId: string,
    cancellationReason?: string
  ): Promise<{ message: string; reservation_id: string }> {
    const params = new URLSearchParams();
    if (cancellationReason) {
      params.append('cancellation_reason', cancellationReason);
    }

    const response = await fetch(
      `${API_BASE_URL}/reservations/${reservationId}?${params}`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || '예약 취소에 실패했습니다');
    }

    return response.json();
  },
};
